import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tabs, type TabItem } from '~/components/ui/Tabs';
import {
  useGetMyBuyLists,
  useGetTeamBuyLists,
  type BuyListQueryParams,
} from '~/api/buyList';
import { useGetTransportVehicles } from '~/api/facility';
import { useGetBuyListLocations } from '~/api/location';
import {
  useGetBuyListProducts,
  useGetMyBuyListProducts,
} from '~/api/platformProducts';
import { useGetOrganizationBuyers } from '~/api/users';
import type { MyBuyListsQuery, TeamBuyListsQuery } from '~/graphql/graphql';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useRouteSort } from '~/hooks/useRouteSort';
import { useBuyListFilters } from '~/pages/buy-lists/lib/useBuyListFilters';
import { useMyBuyListFilters } from '~/pages/buy-lists/lib/useMyBuyListFilters';
import type { BuyListSummary } from '~/pages/buy-lists/sections/BuyListTable';
import { locationPickerLabel } from '~/pages/buy-lists/lib/locationLabel';
import { CreateBuyListModal } from '~/pages/buy-lists/sections/CreateBuyListModal';
import { MyBuyList } from '~/pages/buy-lists/sections/MyBuyList';
import { OrgBuyList } from '~/pages/buy-lists/sections/OrgBuyList';
import type { RootState } from '~/redux/store';
import type { PaginatedResponse } from '~/types';
import { formatNameWithCode } from '~/utils';
import { transportVehicleOptions } from '~/utils/vehicleGroups';

// UI column key -> backend sort-field whitelist entry (BuyListSortJpql). A column absent
// from this map is not server-sortable.
const SORT_FIELDS: Partial<Record<string, string>> = {
  expectedShipDate: 'expectedShipDate',
  products: 'products',
};

function toProductOptions(
  products: { id: string; name: string; code?: string | null }[] | undefined
) {
  return (products ?? []).map((product) => ({
    label: formatNameWithCode(product.name, product.code),
    value: product.id,
  }));
}

export default function BuyLists() {
  const { t } = useTranslation('inventory');
  const filters = useBuyListFilters();
  const myFilters = useMyBuyListFilters();
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    data: buyersData,
    isLoading: isLoadingBuyers,
    error: buyersError,
  } = useGetOrganizationBuyers();
  const { data: locationsData } = useGetBuyListLocations();
  const { data: vehiclesData } = useGetTransportVehicles();
  const { data: productsData, isLoading: isLoadingProducts } =
    useGetBuyListProducts();
  const { data: myProductsData, isLoading: isLoadingMyProducts } =
    useGetMyBuyListProducts();

  const buyers = useMemo(
    () =>
      (buyersData?.users ?? []).filter(
        (buyer) => buyer.status === 'ACTIVE' && buyer.id !== currentUserId
      ),
    [buyersData?.users, currentUserId]
  );

  const locationOptions = useMemo(
    () =>
      (locationsData?.buyListLocations ?? []).map((location) => ({
        label: locationPickerLabel(location),
        value: location.id,
      })),
    [locationsData?.buyListLocations]
  );

  const vehicleOptions = useMemo(
    () =>
      transportVehicleOptions(
        (vehiclesData && vehiclesData.transportVehicles) ?? []
      ),
    [vehiclesData]
  );

  const productOptions = useMemo(
    () => toProductOptions(productsData?.buyListProducts),
    [productsData?.buyListProducts]
  );

  const myProductOptions = useMemo(
    () => toProductOptions(myProductsData?.myBuyListProducts),
    [myProductsData?.myBuyListProducts]
  );

  const myBuyListFilters = useMemo<BuyListQueryParams>(
    () => ({ match: myFilters.match }),
    [myFilters.match]
  );

  const teamFilters = useMemo<BuyListQueryParams>(
    () => ({
      buyerIds: filters.buyerIds,
      match: filters.match,
    }),
    [filters.buyerIds, filters.match]
  );

  const { sort: myBuyListsSort, onSort: onSortMyBuyLists } =
    useRouteSort(SORT_FIELDS);
  const { sort: teamBuyListsSort, onSort: onSortTeamBuyLists } =
    useRouteSort(SORT_FIELDS);

  const {
    data: myBuyLists,
    sentinelRef: myBuyListsSentinelRef,
    isLoading: isLoadingMyBuyLists,
    isLoadingMore: isLoadingMoreMyBuyLists,
    pagination: myBuyListsPaging,
    error: myBuyListsError,
    refetch: refetchMyBuyLists,
  } = usePaginatedQuery<MyBuyListsQuery, BuyListSummary, BuyListQueryParams>({
    fetchHook: useGetMyBuyLists,
    select: (data) => data?.myBuyLists as PaginatedResponse<BuyListSummary>,
    // catchUpOnSortChange: re-sorting must not throw away the rows the buyer has already
    // scrolled to — the list reloads to the same depth in the new order.
    options: {
      filters: myBuyListFilters,
      sort: myBuyListsSort,
      catchUpOnSortChange: true,
      enabled: !myFilters.isLoading,
    },
  });

  const {
    data: teamBuyLists,
    sentinelRef: teamBuyListsSentinelRef,
    isLoading: isLoadingTeamBuyLists,
    isLoadingMore: isLoadingMoreTeamBuyLists,
    pagination: teamBuyListsPaging,
    error: teamBuyListsError,
    refetch: refetchTeamBuyLists,
  } = usePaginatedQuery<TeamBuyListsQuery, BuyListSummary, BuyListQueryParams>({
    fetchHook: useGetTeamBuyLists,
    select: (data) => data?.teamBuyLists as PaginatedResponse<BuyListSummary>,
    options: {
      filters: teamFilters,
      sort: teamBuyListsSort,
      catchUpOnSortChange: true,
      enabled: !filters.isLoading,
    },
  });

  const isTeamListLoading = isLoadingTeamBuyLists || filters.isLoading;

  const tabs: TabItem[] = [
    {
      label: t('inventory.buy_list.my_lists_tab'),
      count: myBuyListsPaging?.totalElements ?? 0,
      link: 'mine',
      component: (
        <MyBuyList
          filters={myFilters}
          locationOptions={locationOptions}
          vehicleOptions={vehicleOptions}
          productOptions={myProductOptions}
          isLoadingProducts={isLoadingMyProducts}
          buyLists={myBuyLists}
          isLoading={isLoadingMyBuyLists || myFilters.isLoading}
          isLoadingMore={isLoadingMoreMyBuyLists}
          sentinelRef={myBuyListsSentinelRef}
          hasFailed={!!myBuyListsError}
          onRetry={() => refetchMyBuyLists?.()}
          onSort={onSortMyBuyLists}
          onCreate={() => setIsCreateOpen(true)}
        />
      ),
    },
    {
      label: t('inventory.buy_list.organization_lists_tab'),
      count: teamBuyListsPaging?.totalElements ?? 0,
      link: 'team',
      component: (
        <OrgBuyList
          filters={filters}
          buyers={buyers}
          isLoadingBuyers={isLoadingBuyers}
          hasBuyersFailed={!!buyersError}
          locationOptions={locationOptions}
          vehicleOptions={vehicleOptions}
          productOptions={productOptions}
          isLoadingProducts={isLoadingProducts}
          buyLists={teamBuyLists}
          isLoading={isTeamListLoading}
          isLoadingMore={isLoadingMoreTeamBuyLists}
          sentinelRef={teamBuyListsSentinelRef}
          hasFailed={!!teamBuyListsError}
          onRetry={() => refetchTeamBuyLists?.()}
          onSort={onSortTeamBuyLists}
        />
      ),
    },
  ];

  return (
    <div className="w-full" data-testid="buy-lists-page">
      <title>{t('inventory.buy_list.page_title')}</title>
      <Tabs
        title={t('inventory.buy_list.page_title')}
        tabs={tabs}
        basePath="buy-lists"
      />
      {isCreateOpen && (
        <CreateBuyListModal onClose={() => setIsCreateOpen(false)} />
      )}
    </div>
  );
}
